import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import HeroSection from './components/HeroSection';
import { categories, getPopularComics, getLatestComics } from '@/mocks/comics';
import { useJsonLd, generateWebsiteSchema, generateOrganizationSchema } from '@/utils/seo';

export default function Home() {
  useJsonLd(generateOrganizationSchema());
  useJsonLd(generateWebsiteSchema());

  const popularComics = getPopularComics(8);
  const latestComics = getLatestComics(8);

  useEffect(() => {
    document.title = '丫丫漫画：下拉式免费阅读-丫丫漫画翻页与丫丫漫画在线看 | 全集漫画流畅体验';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        '丫丫漫画🌟下拉式翻页在线阅读，👀免费畅享全集漫画，韩漫日漫国漫一应俱全，流畅舒适的阅读体验随时追更。丫丫漫画下拉式翻页阅读平台，提供韩漫、日漫、国漫全集免费在线看。'
      );
    }
  }, []);

  const categoryImages: Record<string, string> = {
    guoman: 'https://readdy.ai/api/search-image?query=Chinese%20ink%20painting%20style%20landscape%20with%20mountains%20and%20mist%2C%20ancient%20Chinese%20aesthetic%2C%20warm%20golden%20and%20coral%20tones%2C%20artistic%20abstract%20composition%2C%20elegant%20atmosphere&width=600&height=400&seq=cat-guoman&orientation=landscape',
    riman: 'https://readdy.ai/api/search-image?query=Japanese%20anime%20style%20cherry%20blossom%20scene%20with%20soft%20pastel%20pink%20and%20cream%20colors%2C%20dreamy%20atmosphere%2C%20artistic%20illustration%2C%20clean%20composition%2C%20gentle%20lighting&width=600&height=400&seq=cat-riman&orientation=landscape',
    hanman: 'https://readdy.ai/api/search-image?query=Korean%20modern%20cityscape%20at%20twilight%20with%20warm%20amber%20and%20rose%20tones%2C%20artistic%20digital%20illustration%2C%20clean%20composition%2C%20sophisticated%20urban%20vibe&width=600&height=400&seq=cat-hanman&orientation=landscape',
    meiman: 'https://readdy.ai/api/search-image?query=American%20comic%20book%20style%20heroic%20cityscape%20with%20bold%20colors%2C%20dramatic%20lighting%2C%20dynamic%20composition%2C%20artistic%20illustration%2C%20clean%20graphic%20style&width=600&height=400&seq=cat-meiman&orientation=landscape',
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <HeroSection />

        <section className="px-4 md:px-6 py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">热门推荐</h2>
              <p className="mt-2 text-sm text-foreground-500">千万读者都在追的精彩漫画</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
              {popularComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 px-6 py-2.5 border border-primary-300 text-primary-600 rounded-full text-sm font-medium hover:bg-primary-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">漫画分类</h2>
              <p className="mt-2 text-sm text-foreground-500">找到你喜欢的漫画类型</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {categories.filter((c) => c.slug !== 'all').map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group relative rounded-xl overflow-hidden aspect-[3/2] cursor-pointer block"
                >
                  <img
                    src={categoryImages[cat.slug] || categoryImages.guoman}
                    alt={`${cat.name}漫画`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">{cat.name}</h3>
                    <p className="text-xs text-white/70 mt-0.5">点击进入专区</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">最近更新</h2>
              <p className="mt-2 text-sm text-foreground-500">新鲜出炉，马上追更</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
              {latestComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-16 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4">
              为什么选择丫丫漫画？
            </h2>
            <p className="text-sm text-foreground-500 mb-10">用心打造最佳漫画阅读体验</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-xl bg-background-50">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mx-auto mb-4">
                  <i className="ri-smartphone-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-2">下拉式翻页</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">创新的下拉式翻页阅读，无需点击，像刷短视频一样流畅看漫画。</p>
              </div>
              <div className="p-6 rounded-xl bg-background-50">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-4">
                  <i className="ri-book-open-line text-2xl text-accent-500"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-2">海量资源</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">国漫、日漫、韩漫应有尽有，每日更新，保证你永远有漫画可看。</p>
              </div>
              <div className="p-6 rounded-xl bg-background-50">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-200 mx-auto mb-4">
                  <i className="ri-heart-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-2">完全免费</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">所有漫画免费阅读，无需付费订阅。只为让更多人享受漫画的乐趣。</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}